import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  createStyles,
  Divider,
  InputLabel,
  ListItemText,
  MenuItem,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import { DateTime } from "luxon";
import { FORMAT_TYPE } from "utils/DateTimeUtils";
import Select from "@material-ui/core/Select";
import Input from "@material-ui/core/Input";
import Checkbox from "@material-ui/core/Checkbox";
import GuestGenreType from "types/enum/GuestGenreType";
import GuestAnalyticsGroupType from "types/enum/GuestAnalyticsGroupType";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { fetchShops } from "redux/actions/shop";
import { fetchArea } from "redux/actions/area";
import { simpleFetchGuestCategory } from "redux/actions/guestCategory";
import { fetchNotelClass } from "redux/actions/notelClass";
import { Bar, BarChart, Brush, Tooltip, XAxis, YAxis } from "recharts";
import Modal from "components/Modal";
import useModal from "hooks/useModal";
import GuestAnalyticsSalesVolumeApi from "api/GuestAnalyticsSalesVolumeApi";
import { fetchCourse } from "redux/actions/course";
import Table from "@material-ui/core/Table";
import MathUtils from "utils/MathUtils";
import { useTranslation } from "react-i18next";

const GuestAnalyticsSalesVolume = () => {
  const { t } = useTranslation("guestAnalytics");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const changeDateTime = useSelector(
    (state) => state.account.staff.company.changeDateTime
  );
  const changeDate = DateTime.fromFormat(changeDateTime, "HH:mm:ss");
  const shops = useSelector((state) => state.shop);
  const notelClasses = useSelector((state) => state.notelClass);
  const guestCategories = useSelector((state) => state.guestCategory);
  const courses = useSelector((state) => state.course);
  const areas = useSelector((state) => state.area);
  const [endDate, setEndDate] = useState(
    DateTime.local()
      .minus({ hours: changeDate.hour, minutes: changeDate.minute })
      .toJSDate()
  );
  const [selectShops, setSelectShops] = useState<string[]>([]);
  const [selectNotelClasses, setSelectNotelClasses] = useState<string[]>([]);
  const [selectGuestCategories, setSelectGuestCategories] = useState<string[]>(
    []
  );
  const [selectArea, setSelectArea] = useState<string[]>([]);
  const [selectGenre, setSelectGenre] = useState<string[]>([
    ...Object.keys(GuestGenreType),
    "すべて",
  ]);
  const [selectGroup, setSelectGroup] = useState<string[]>([
    ...Object.keys(GuestAnalyticsGroupType),
    "すべて",
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [salesData, setSalesData] = useState<{
    [key: string]: { [key: string]: any };
  }>({});
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        margin: theme.spacing(1),
      },
    })
  );
  const classes = useStyles();
  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchArea(companyId));
    dispatch(simpleFetchGuestCategory(companyId));
    dispatch(fetchNotelClass(companyId));
    dispatch(fetchCourse(companyId));
  }, []);

  useEffect(() => {
    setSelectShops([...shops.map((shop) => String(shop.shopId)), "すべて"]);
  }, [shops]);

  useEffect(() => {
    setSelectNotelClasses([
      ...notelClasses.map((notelClass) => String(notelClass.notelClassId)),
      "すべて",
    ]);
  }, [notelClasses]);

  useEffect(() => {
    setSelectGuestCategories([
      ...guestCategories.map((guestCategory) =>
        String(guestCategory.guestCategoryId)
      ),
      "すべて",
    ]);
  }, [guestCategories]);

  useEffect(() => {
    setSelectArea([...areas.map((area) => String(area.areaId)), "すべて"]);
  }, [areas]);

  const onClickSearch = async () => {
    setIsLoading(true);
    const stringEndDate = DateTime.fromJSDate(endDate).toFormat(
      FORMAT_TYPE.YEAR_DAY
    );
    const result = await GuestAnalyticsSalesVolumeApi.findData(companyId, {
      shopIds: selectShops,
      endDate: stringEndDate,
      guestCategoryIds: selectGuestCategories,
      notelClassIds: selectNotelClasses,
      areaIds: selectArea,
      guestAnalyticsType: selectGroup,
      guestGenreType: selectGenre,
    });
    setSalesData(result);
    setIsLoading(false);
  };

  return (
    <Box display="flex" flexDirection={"column"}>
      <Box margin={2} display="flex" alignItems="center">
        <TextField
          type="date"
          label={t("filters.endDate")}
          style={{ margin: "10px", width: "200px" }}
          value={DateTime.fromJSDate(endDate).toFormat(FORMAT_TYPE.YEAR_DAY)}
          onChange={(event) =>
            setEndDate(
              DateTime.fromISO(event.target.value as string).toJSDate()
            )
          }
        />
        <Box>
          <InputLabel id="select-multiple-shop">{t("filters.shop")}</InputLabel>
          <Select
            multiple
            value={selectShops}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setSelectShops((prev: string[]) => {
                if (
                  prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [];
                } else if (
                  !prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") !== -1
                ) {
                  return [
                    ...shops.map((shop) => String(shop.shopId)),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length === shops.length &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [
                    ...shops.map((shop) => String(shop.shopId)),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length <= shops.length
                ) {
                  return (event.target.value as string[]).filter(
                    (name) => name !== "すべて"
                  );
                } else {
                  return event.target.value as string[];
                }
              });
            }}
            input={<Input id="select-multiple-shop" />}
            style={{ width: "200px" }}
            renderValue={(selected) => {
              if ((selected as string[]).includes("すべて")) {
                return t("filters.all");
              } else {
                return shops
                  .filter((shop) =>
                    (selected as string[]).includes(String(shop.shopId))
                  )
                  .map((shop) => shop.name)
                  .join(", ");
              }
            }}
          >
            <MenuItem key={"すべて"} value={"すべて"}>
              <Checkbox
                name="all"
                checked={selectShops.indexOf("すべて") > -1}
              />
              <ListItemText primary={t("filters.all")} />
            </MenuItem>
            {shops.map((shop) => (
              <MenuItem key={shop.shopId} value={String(shop.shopId)}>
                <Checkbox
                  checked={selectShops.indexOf(String(shop.shopId)) !== -1}
                />
                <ListItemText primary={shop.name} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <InputLabel id="select-multiple-notel-class">{t("filters.notelClass")}</InputLabel>
          <Select
            multiple
            value={selectNotelClasses}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setSelectNotelClasses((prev: string[]) => {
                if (
                  prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [];
                } else if (
                  !prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") !== -1
                ) {
                  return [
                    ...notelClasses.map((notelClass) =>
                      String(notelClass.notelClassId)
                    ),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length ===
                    notelClasses.length &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [
                    ...notelClasses.map((notelClass) =>
                      String(notelClass.notelClassId)
                    ),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length <= notelClasses.length
                ) {
                  return (event.target.value as string[]).filter(
                    (name) => name !== "すべて"
                  );
                } else {
                  return event.target.value as string[];
                }
              });
            }}
            input={<Input id="select-multiple-notelClass" />}
            style={{ width: "200px", marginRight: "10px" }}
            renderValue={(selected) => {
              if ((selected as string[]).includes("すべて")) {
                return t("filters.all");
              } else {
                return notelClasses
                  .filter((notelClass) =>
                    (selected as string[]).includes(
                      String(notelClass.notelClassId)
                    )
                  )
                  .map((notelClass) => notelClass.name)
                  .join(", ");
              }
            }}
          >
            <MenuItem key={"すべて"} value={"すべて"}>
              <Checkbox
                name="all"
                checked={selectNotelClasses.indexOf("すべて") > -1}
              />
              <ListItemText primary={t("filters.all")} />
            </MenuItem>
            {notelClasses.map((notelClass) => (
              <MenuItem
                key={notelClass.notelClassId}
                value={String(notelClass.notelClassId)}
              >
                <Checkbox
                  checked={
                    selectNotelClasses.indexOf(
                      String(notelClass.notelClassId)
                    ) !== -1
                  }
                />
                <ListItemText primary={notelClass.name} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <InputLabel id="select-multiple-shop">{t("filters.guestCategory")}</InputLabel>
          <Select
            multiple
            value={selectGuestCategories}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setSelectGuestCategories((prev: string[]) => {
                if (
                  prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [];
                } else if (
                  !prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") !== -1
                ) {
                  return [
                    ...guestCategories.map((guestCategory) =>
                      String(guestCategory.guestCategoryId)
                    ),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length ===
                    guestCategories.length &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [
                    ...guestCategories.map((guestCategory) =>
                      String(guestCategory.guestCategoryId)
                    ),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length <=
                  guestCategories.length
                ) {
                  return (event.target.value as string[]).filter(
                    (name) => name !== "すべて"
                  );
                } else {
                  return event.target.value as string[];
                }
              });
            }}
            input={<Input id="select-multiple-guest-category" />}
            style={{ width: "200px", marginRight: "10px" }}
            renderValue={(selected) => {
              if ((selected as string[]).includes("すべて")) {
                return t("filters.all");
              } else {
                return guestCategories
                  .filter((guestCategory) =>
                    (selected as string[]).includes(
                      String(guestCategory.guestCategoryId)
                    )
                  )
                  .map((guestCategory) => guestCategory.name)
                  .join(", ");
              }
            }}
          >
            <MenuItem key={"すべて"} value={"すべて"}>
              <Checkbox
                name="all"
                checked={selectGuestCategories.indexOf("すべて") > -1}
              />
              <ListItemText primary={t("filters.all")} />
            </MenuItem>
            {guestCategories.map((guestCategory) => (
              <MenuItem
                key={guestCategory.guestCategoryId}
                value={String(guestCategory.guestCategoryId)}
              >
                <Checkbox
                  checked={
                    selectGuestCategories.indexOf(
                      String(guestCategory.guestCategoryId)
                    ) !== -1
                  }
                />
                <ListItemText primary={guestCategory.name} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <InputLabel id="select-multiple-guest-genre">{t("filters.guestGenre")}</InputLabel>
          <Select
            multiple
            value={selectGenre}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setSelectGenre((prev: string[]) => {
                if (
                  prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [];
                } else if (
                  !prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") !== -1
                ) {
                  return [...Object.keys(GuestGenreType), "すべて"];
                } else if (
                  (event.target.value as string[]).length ===
                    Object.keys(GuestGenreType).length &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [...Object.keys(GuestGenreType), "すべて"];
                } else if (
                  (event.target.value as string[]).length <=
                  Object.keys(GuestGenreType).length
                ) {
                  return (event.target.value as string[]).filter(
                    (name) => name !== "すべて"
                  );
                } else {
                  return event.target.value as string[];
                }
              });
            }}
            input={<Input id="select-multiple-guest-genre" />}
            style={{ margin: "10px", width: "200px" }}
            renderValue={(selected) => {
              if ((selected as string[]).includes("すべて")) {
                return t("filters.all");
              } else {
                return Object.keys(GuestGenreType)
                  .filter((name) => (selected as string[]).includes(name))
                  .map((key) => (GuestGenreType as any)[key])
                  .join(", ");
              }
            }}
          >
            <MenuItem key={"すべて"} value={"すべて"}>
              <Checkbox
                name="all"
                checked={selectGenre.indexOf("すべて") > -1}
              />
              <ListItemText primary={t("filters.all")} />
            </MenuItem>
            {Object.entries(GuestGenreType).map(([key, name]) => (
              <MenuItem key={key} value={String(key)}>
                <Checkbox checked={selectGenre.indexOf(String(key)) !== -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <InputLabel id="select-multiple-guest-genre">{t("filters.guestGroup")}</InputLabel>
          <Select
            multiple
            value={selectGroup}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setSelectGroup((prev: string[]) => {
                if (
                  prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [];
                } else if (
                  !prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") !== -1
                ) {
                  return [...Object.keys(GuestAnalyticsGroupType), "すべて"];
                } else if (
                  (event.target.value as string[]).length ===
                    Object.keys(GuestAnalyticsGroupType).length &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [...Object.keys(GuestAnalyticsGroupType), "すべて"];
                } else if (
                  (event.target.value as string[]).length <=
                  Object.keys(GuestAnalyticsGroupType).length
                ) {
                  return (event.target.value as string[]).filter(
                    (name) => name !== "すべて"
                  );
                } else {
                  return event.target.value as string[];
                }
              });
            }}
            input={<Input id="select-multiple-guest-genre" />}
            style={{ width: "200px" }}
            renderValue={(selected) => {
              if ((selected as string[]).includes("すべて")) {
                return t("filters.all");
              } else {
                return Object.keys(GuestAnalyticsGroupType)
                  .filter((name) => (selected as string[]).includes(name))
                  .map((key) => (GuestAnalyticsGroupType as any)[key])
                  .join(", ");
              }
            }}
          >
            <MenuItem key={"すべて"} value={"すべて"}>
              <Checkbox
                name="all"
                checked={selectGroup.indexOf("すべて") > -1}
              />
              <ListItemText primary={t("filters.all")} />
            </MenuItem>
            {Object.entries(GuestAnalyticsGroupType).map(([key, name]) => (
              <MenuItem key={key} value={String(key)}>
                <Checkbox checked={selectGroup.indexOf(String(key)) !== -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <InputLabel id="select-multiple-area">{t("filters.area")}</InputLabel>
          <Select
            multiple
            value={selectArea}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setSelectArea((prev: string[]) => {
                if (
                  prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [];
                } else if (
                  !prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") !== -1
                ) {
                  return [
                    ...areas.map((area) => String(area.areaId)),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length === areas.length &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [
                    ...areas.map((area) => String(area.areaId)),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length <= areas.length
                ) {
                  return (event.target.value as string[]).filter(
                    (name) => name !== "すべて"
                  );
                } else {
                  return event.target.value as string[];
                }
              });
            }}
            input={<Input id="select-multiple-area" />}
            style={{ width: "200px", marginRight: "10px" }}
            renderValue={(selected) => {
              if ((selected as string[]).includes("すべて")) {
                return t("filters.all");
              } else {
                return areas
                  .filter((area) =>
                    (selected as string[]).includes(String(area.areaId))
                  )
                  .map((area) => area.name)
                  .join(", ");
              }
            }}
          >
            <MenuItem key={"すべて"} value={"すべて"}>
              <Checkbox
                name="all"
                checked={selectArea.indexOf("すべて") > -1}
              />
              <ListItemText primary={t("filters.all")} />
            </MenuItem>
            {areas.map((area) => (
              <MenuItem key={area.areaId} value={String(area.areaId)}>
                <Checkbox
                  checked={selectArea.indexOf(String(area.areaId)) !== -1}
                />
                <ListItemText primary={area.name} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Button
          variant="contained"
          className={classes.button}
          color="primary"
          onClick={onClickSearch}
        >
          {t("actions.search")}
        </Button>
      </Box>
      {isLoading ? (
        <CircularProgress />
      ) : Object.keys(salesData).length ? (
        <>
          <Box
            paddingY={3}
            display={"flex"}
            justifyContent={"space-evenly"}
            style={{ flexWrap: "wrap" }}
          >
            {Object.entries(salesData).map(([key, data]) => {
              switch (key) {
                case "shop": {
                  return (
                    <Box display={"flex"} flexDirection={"column"}>
                      <Box
                        display={"flex"}
                        justifyContent={"space-evenly"}
                        width={"1500px"}
                      >
                        <ShopYearGraph data={data} />
                        <ShopMonthGraph data={data} />
                      </Box>
                      <Box
                        display={"flex"}
                        justifyContent={"space-evenly"}
                        marginTop={1}
                        width={"1500px"}
                      >
                        <SalesVolumeTable
                          title={t("chart.shopYearCompare")}
                          header={[
                            { name: t("chart.shopName"), key: "shopName" },
                            { name: t("chart.current12Months"), key: "current" },
                            { name: t("chart.previous12Months"), key: "last" },
                            { name: t("chart.yearOverYear"), key: "diff" },
                            { name: t("chart.yearGrowthRate"), key: "growth" },
                          ]}
                          data={Object.entries(data).map(([key, d]) => ({
                            name:
                              shops.find((shop) => shop.shopId === Number(key))
                                ?.name || "",
                            current: d.thisYear,
                            last: d.lastYear,
                          }))}
                        />
                        <SalesVolumeTable
                          title={t("chart.shopMonthCompare")}
                          header={[
                            { name: t("chart.shopName"), key: "shopName" },
                            { name: t("chart.thisMonthSales"), key: "current" },
                            { name: t("chart.lastMonthSales"), key: "last" },
                            { name: t("chart.monthOverMonth"), key: "diff" },
                            { name: t("chart.monthGrowthRate"), key: "growth" },
                          ]}
                          data={Object.entries(data).map(([key, d]) => ({
                            name:
                              shops.find((shop) => shop.shopId === Number(key))
                                ?.name || "",
                            current: d.thisMonth,
                            last: d.lastMonth,
                          }))}
                        />
                      </Box>
                    </Box>
                  );
                }
                case "course": {
                  return (
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      marginTop={"100px"}
                    >
                      <Box
                        display={"flex"}
                        justifyContent={"space-evenly"}
                        width={"1500px"}
                      >
                        <CourseYearGraph data={data} />
                        <CourseMonthGraph data={data} />
                      </Box>
                      <Box
                        display={"flex"}
                        justifyContent={"space-evenly"}
                        marginTop={1}
                        width={"1500px"}
                      >
                        <SalesVolumeTable
                          title={t("chart.courseYearCompare")}
                          header={[
                            { name: t("chart.courseName"), key: "courseName" },
                            { name: t("chart.current12Months"), key: "current" },
                            { name: t("chart.previous12Months"), key: "last" },
                            { name: t("chart.yearOverYear"), key: "diff" },
                            { name: t("chart.yearGrowthRate"), key: "growth" },
                          ]}
                          data={Object.entries(data).map(([key, d]) => ({
                            name:
                              courses.find(
                                (course) => course.courseId === Number(key)
                              )?.name || "",
                            current: d.thisYear,
                            last: d.lastYear,
                          }))}
                        />
                        <SalesVolumeTable
                          title={t("chart.courseMonthCompare")}
                          header={[
                            { name: t("chart.courseName"), key: "courseName" },
                            { name: t("chart.thisMonthSales"), key: "current" },
                            { name: t("chart.lastMonthSales"), key: "last" },
                            { name: t("chart.monthOverMonth"), key: "diff" },
                            { name: t("chart.monthGrowthRate"), key: "growth" },
                          ]}
                          data={Object.entries(data).map(([key, d]) => ({
                            name:
                              courses.find(
                                (course) => course.courseId === Number(key)
                              )?.name || "",
                            current: d.thisMonth,
                            last: d.lastMonth,
                          }))}
                        />
                      </Box>
                    </Box>
                  );
                }
                case "notelClass": {
                  return (
                    <>
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        marginTop={"100px"}
                      >
                        <Box
                          display={"flex"}
                          justifyContent={"space-evenly"}
                          width={"1500px"}
                        >
                          <NotelClassYearGraph data={data} />
                          <NotelClassMonthGraph data={data} />
                        </Box>
                        <Box
                          display={"flex"}
                          justifyContent={"space-evenly"}
                          marginTop={1}
                          width={"1500px"}
                        >
                          <SalesVolumeTable
                            title={t("chart.classYearCompare")}
                            header={[
                              { name: t("chart.className"), key: "notelClassName" },
                              { name: t("chart.current12Months"), key: "current" },
                              { name: t("chart.previous12Months"), key: "last" },
                              { name: t("chart.yearOverYear"), key: "diff" },
                              { name: t("chart.yearGrowthRate"), key: "growth" },
                            ]}
                            data={Object.entries(data).map(([key, d]) => ({
                              name:
                                notelClasses.find(
                                  (notelClass) =>
                                    notelClass.notelClassId === Number(key)
                                )?.name || "",
                              current: d.thisYear,
                              last: d.lastYear,
                            }))}
                          />
                          <SalesVolumeTable
                            title={t("chart.classMonthCompare")}
                            header={[
                              { name: t("chart.className"), key: "notelClassName" },
                              { name: t("chart.thisMonthSales"), key: "current" },
                              { name: t("chart.lastMonthSales"), key: "last" },
                              { name: t("chart.monthOverMonth"), key: "diff" },
                              { name: t("chart.monthGrowthRate"), key: "growth" },
                            ]}
                            data={Object.entries(data).map(([key, d]) => ({
                              name:
                                notelClasses.find(
                                  (notelClass) =>
                                    notelClass.notelClassId === Number(key)
                                )?.name || "",
                              current: d.thisMonth,
                              last: d.lastMonth,
                            }))}
                          />
                        </Box>
                      </Box>
                    </>
                  );
                }
              }
            })}
          </Box>
        </>
      ) : (
        <Box display={"flex"} justifyContent={"center"}>
          <Typography>
            {t("empty.noDataRetry")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

type GraphProps = { data: { [key: string]: any } };
const ShopYearGraph = ({ data }: GraphProps) => {
  const { t } = useTranslation("guestAnalytics");
  const shops = useSelector((state) => state.shop);
  const [sortKey, setSortKey] = useState("thisYear");
  const [, setNonSalesAreaModal] = useModal("nonSalesAreaModal");
  const formatData: any[] = [];
  const noSalesArea: string[] = [];
  Object.entries(data)
    .sort(([, a], [, b]) => {
      if (sortKey === "thisYear") {
        return a.thisYear > b.thisYear ? -1 : 1;
      } else {
        return a.lastYear > b.lastYear ? -1 : 1;
      }
    })
    .forEach(([category, d]) => {
      if (!d.thisYear && !d.lastYear) {
        noSalesArea.push(
          shops.find((shop) => shop.shopId === Number(category))?.name || ""
        );
      } else {
        formatData.push({
          name:
            shops.find((shop) => shop.shopId === Number(category))?.name || "",
          ...d,
        });
      }
    });
  const openModal = () => {
    setNonSalesAreaModal(true, {
      title: t("chart.zeroSales"),
      areaNames: noSalesArea,
    });
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      marginX={2}
      marginY={1}
      width={700}
    >
      <Box display={"flex"} alignItems={"center"}>
        <Typography variant={"subtitle1"}>{t("chart.shopYearCompare")}</Typography>
        <Typography
          variant={"body1"}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            width: "100px",
            margin: "0 10px",
          }}
          onClick={openModal}
        >
          {t("chart.zeroSales")}
        </Typography>
        <TextField
          select
          value={sortKey || ""}
          onChange={(event) => setSortKey(event.target.value as string)}
        >
          <MenuItem value={"thisYear"} key={"thisYear"}>
            {t("chart.sortByThisYear")}
          </MenuItem>
          <MenuItem value={"lastYear"} key={"lastYear"}>
            {t("chart.sortByLastYear")}
          </MenuItem>
        </TextField>
      </Box>
      {formatData.length ? (
        <>
          <BarChart width={700} height={500} data={formatData}>
            <YAxis type="number" width={150} />
            <XAxis
              dataKey="name"
              type="category"
              tick={<CustomizeShopTick />}
              minTickGap={1}
              interval={0}
              height={80}
            />
            <Bar
              dataKey="thisYear"
              fill="#8884d8"
              name={t("chart.last12Months")}
              barSize={20}
            />
            <Bar
              dataKey="lastYear"
              fill="#FCD361"
              name={t("chart.previousYear12Months")}
              barSize={20}
            />
            <Tooltip formatter={(value: any) => `¥${value}`} />
            {formatData.length > 10 && (
              <Brush height={30} endIndex={10} dataKey={"name"} />
            )}
          </BarChart>
          <NonSalesAreaModal />
        </>
      ) : (
        <Typography>{t("empty.noData")}</Typography>
      )}
    </Box>
  );
};

const ShopMonthGraph = ({ data }: GraphProps) => {
  const { t } = useTranslation("guestAnalytics");
  const shops = useSelector((state) => state.shop);
  const [sortKey, setSortKey] = useState("thisMonth");
  const [, setNonSalesAreaModal] = useModal("nonSalesAreaModal");
  const formatData: any[] = [];
  const noSalesArea: string[] = [];
  Object.entries(data)
    .sort(([, a], [, b]) => {
      if (sortKey === "thisMonth") {
        return a.thisMonth > b.thisMonth ? -1 : 1;
      } else {
        return a.lastMonth > b.lastMonth ? -1 : 1;
      }
    })
    .forEach(([category, d]) => {
      if (!d.thisMonth && !d.lastMonth) {
        noSalesArea.push(
          shops.find((shop) => shop.shopId === Number(category))?.name || ""
        );
      } else {
        formatData.push({
          name:
            shops.find((shop) => shop.shopId === Number(category))?.name || "",
          ...d,
        });
      }
    });
  const openModal = () => {
    setNonSalesAreaModal(true, {
      title: t("chart.zeroSales"),
      areaNames: noSalesArea,
    });
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      marginX={2}
      marginY={1}
      width={700}
    >
      <Box display={"flex"} alignItems={"center"}>
        <Typography variant={"subtitle1"}>{t("chart.shopMonthCompare")}</Typography>
        <Typography
          variant={"body1"}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            width: "100px",
            margin: "0 10px",
          }}
          onClick={openModal}
        >
          {t("chart.zeroSales")}
        </Typography>
        <TextField
          select
          value={sortKey || ""}
          onChange={(event) => setSortKey(event.target.value as string)}
        >
          <MenuItem value={"thisMonth"} key={"thisMonth"}>
            {t("chart.sortByThisYear")}
          </MenuItem>
          <MenuItem value={"lastMonth"} key={"lastMonth"}>
            {t("chart.sortByLastYear")}
          </MenuItem>
        </TextField>
      </Box>
      {formatData.length ? (
        <>
          <BarChart width={700} height={500} data={formatData}>
            <YAxis type="number" width={150} />
            <XAxis
              dataKey="name"
              type="category"
              tick={<CustomizeShopTick />}
              minTickGap={1}
              interval={0}
              height={80}
            />
            <Bar
              dataKey="thisMonth"
              fill="#8884d8"
              name={t("chart.last12Months")}
              barSize={20}
            />
            <Bar
              dataKey="lastMonth"
              fill="#FCD361"
              name={t("chart.previousYear12Months")}
              barSize={20}
            />
            <Tooltip formatter={(value: any) => `¥${value}`} />
            {formatData.length > 10 && (
              <Brush height={30} endIndex={10} dataKey={"name"} />
            )}
          </BarChart>
          <NonSalesAreaModal />
        </>
      ) : (
        <Typography>{t("empty.noData")}</Typography>
      )}
    </Box>
  );
};

const CourseYearGraph = ({ data }: GraphProps) => {
  const { t } = useTranslation("guestAnalytics");
  const courses = useSelector((state) => state.course);
  const [sortKey, setSortKey] = useState("thisYear");
  const [, setNonSalesAreaModal] = useModal("nonSalesAreaModal");
  const formatData: any[] = [];
  const noSalesArea: string[] = [];
  Object.entries(data)
    .sort(([, a], [, b]) => {
      if (sortKey === "thisYear") {
        return a.thisYear > b.thisYear ? -1 : 1;
      } else {
        return a.lastYear > b.lastYear ? -1 : 1;
      }
    })
    .forEach(([category, d]) => {
      if (!d.thisYear && !d.lastYear) {
        noSalesArea.push(
          courses.find((course) => course.courseId === Number(category))
            ?.name || ""
        );
      } else {
        formatData.push({
          name:
            courses.find((course) => course.courseId === Number(category))
              ?.name || "",
          ...d,
        });
      }
    });
  const openModal = () => {
    setNonSalesAreaModal(true, {
      title: t("chart.zeroSales"),
      areaNames: noSalesArea,
    });
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      marginX={2}
      marginY={1}
      width={700}
    >
      <Box display={"flex"} alignItems={"center"}>
        <Typography variant={"subtitle1"}>
          {t("chart.courseYearCompare")}
        </Typography>
        <Typography
          variant={"body1"}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            width: "100px",
            margin: "0 10px",
          }}
          onClick={openModal}
        >
          {t("chart.zeroSales")}
        </Typography>
        <TextField
          select
          value={sortKey || ""}
          onChange={(event) => setSortKey(event.target.value as string)}
        >
          <MenuItem value={"thisYear"} key={"thisYear"}>
            {t("chart.sortByThisYear")}
          </MenuItem>
          <MenuItem value={"lastYear"} key={"lastYear"}>
            {t("chart.sortByLastYear")}
          </MenuItem>
        </TextField>
      </Box>
      {formatData.length ? (
        <>
          <BarChart width={700} height={500} data={formatData}>
            <YAxis type="number" width={150} />
            <XAxis
              dataKey="name"
              type="category"
              tick={<CustomizeShopTick />}
              minTickGap={1}
              interval={0}
              height={80}
            />
            <Bar
              dataKey="thisYear"
              fill="#8884d8"
              name={t("chart.last12Months")}
              barSize={20}
            />
            <Bar
              dataKey="lastYear"
              fill="#FCD361"
              name={t("chart.previousYear12Months")}
              barSize={20}
            />
            <Tooltip formatter={(value: any) => `¥${value}`} />
            {formatData.length > 10 && (
              <Brush height={30} endIndex={10} dataKey={"name"} />
            )}
          </BarChart>
          <NonSalesAreaModal />
        </>
      ) : (
        <Typography>{t("empty.noData")}</Typography>
      )}
    </Box>
  );
};

const CourseMonthGraph = ({ data }: GraphProps) => {
  const { t } = useTranslation("guestAnalytics");
  const courses = useSelector((state) => state.course);
  const [sortKey, setSortKey] = useState("thisMonth");
  const [, setNonSalesAreaModal] = useModal("nonSalesAreaModal");
  const formatData: any[] = [];
  const noSalesArea: string[] = [];
  Object.entries(data)
    .sort(([, a], [, b]) => {
      if (sortKey === "thisMonth") {
        return a.thisMonth > b.thisMonth ? -1 : 1;
      } else {
        return a.lastMonth > b.lastMonth ? -1 : 1;
      }
    })
    .forEach(([category, d]) => {
      if (!d.thisMonth && !d.lastMonth) {
        noSalesArea.push(
          courses.find((course) => course.courseId === Number(category))
            ?.name || ""
        );
      } else {
        formatData.push({
          name:
            courses.find((course) => course.courseId === Number(category))
              ?.name || "",
          ...d,
        });
      }
    });
  const openModal = () => {
    setNonSalesAreaModal(true, {
      title: t("chart.zeroSales"),
      areaNames: noSalesArea,
    });
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      marginX={2}
      marginY={1}
      width={700}
    >
      <Box display={"flex"} alignItems={"center"}>
        <Typography variant={"subtitle1"}>{t("chart.courseMonthCompare")}</Typography>
        <Typography
          variant={"body1"}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            width: "100px",
            margin: "0 10px",
          }}
          onClick={openModal}
        >
          {t("chart.zeroSales")}
        </Typography>
        <TextField
          select
          value={sortKey || ""}
          onChange={(event) => setSortKey(event.target.value as string)}
        >
          <MenuItem value={"thisMonth"} key={"thisMonth"}>
            {t("chart.sortByThisMonth")}
          </MenuItem>
          <MenuItem value={"lastMonth"} key={"lastMonth"}>
            {t("chart.sortByLastMonth")}
          </MenuItem>
        </TextField>
      </Box>
      {formatData.length ? (
        <>
          <BarChart width={700} height={500} data={formatData}>
            <YAxis type="number" width={150} />
            <XAxis
              dataKey="name"
              type="category"
              tick={<CustomizeShopTick />}
              minTickGap={1}
              interval={0}
              height={80}
            />
            <Bar
              dataKey="thisMonth"
              fill="#8884d8"
              name={t("chart.thisMonth")}
              barSize={20}
            />
            <Bar
              dataKey="lastMonth"
              fill="#FCD361"
              name={t("chart.lastMonth")}
              barSize={20}
            />
            <Tooltip formatter={(value: any) => `¥${value}`} />
            {formatData.length > 10 && (
              <Brush height={30} endIndex={10} dataKey={"name"} />
            )}
          </BarChart>
          <NonSalesAreaModal />
        </>
      ) : (
        <Typography>{t("empty.noData")}</Typography>
      )}
    </Box>
  );
};

const NotelClassYearGraph = ({ data }: GraphProps) => {
  const { t } = useTranslation("guestAnalytics");
  const notelClasses = useSelector((state) => state.notelClass);
  const [sortKey, setSortKey] = useState("thisYear");
  const [, setNonSalesAreaModal] = useModal("nonSalesAreaModal");
  const formatData: any[] = [];
  const noSalesArea: string[] = [];
  Object.entries(data)
    .sort(([, a], [, b]) => {
      if (sortKey === "thisYear") {
        return a.thisYear > b.thisYear ? -1 : 1;
      } else {
        return a.lastYear > b.lastYear ? -1 : 1;
      }
    })
    .forEach(([category, d]) => {
      if (!d.thisYear && !d.lastYear) {
        noSalesArea.push(
          notelClasses.find(
            (notelClass) => notelClass.notelClassId === Number(category)
          )?.name || ""
        );
      } else {
        formatData.push({
          name:
            notelClasses.find(
              (notelClass) => notelClass.notelClassId === Number(category)
            )?.name || "",
          ...d,
        });
      }
    });
  const openModal = () => {
    setNonSalesAreaModal(true, {
      title: t("chart.zeroSales"),
      areaNames: noSalesArea,
    });
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      marginX={2}
      marginY={1}
      width={700}
    >
      <Box display={"flex"} alignItems={"center"}>
        <Typography variant={"subtitle1"}>
          {t("chart.classYearCompare")}
        </Typography>
        <Typography
          variant={"body1"}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            width: "100px",
            margin: "0 10px",
          }}
          onClick={openModal}
        >
          {t("chart.zeroSales")}
        </Typography>
        <TextField
          select
          value={sortKey || ""}
          onChange={(event) => setSortKey(event.target.value as string)}
        >
          <MenuItem value={"thisYear"} key={"thisYear"}>
            {t("chart.sortByThisYear")}
          </MenuItem>
          <MenuItem value={"lastYear"} key={"lastYear"}>
            {t("chart.sortByLastYear")}
          </MenuItem>
        </TextField>
      </Box>
      {formatData.length ? (
        <>
          <BarChart width={700} height={500} data={formatData}>
            <YAxis type="number" width={150} />
            <XAxis
              dataKey="name"
              type="category"
              tick={<CustomizeShopTick />}
              minTickGap={1}
              interval={0}
              height={80}
            />
            <Bar
              dataKey="thisYear"
              fill="#8884d8"
              name={t("chart.last12Months")}
              barSize={20}
            />
            <Bar
              dataKey="lastYear"
              fill="#FCD361"
              name={t("chart.previousYear12Months")}
              barSize={20}
            />
            <Tooltip formatter={(value: any) => `¥${value}`} />
            {formatData.length > 10 && (
              <Brush height={30} endIndex={10} dataKey={"name"} />
            )}
          </BarChart>
          <NonSalesAreaModal />
        </>
      ) : (
        <Typography>{t("empty.noData")}</Typography>
      )}
    </Box>
  );
};

const NotelClassMonthGraph = ({ data }: GraphProps) => {
  const { t } = useTranslation("guestAnalytics");
  const notelClasses = useSelector((state) => state.notelClass);
  const [sortKey, setSortKey] = useState("thisMonth");
  const [, setNonSalesAreaModal] = useModal("nonSalesAreaModal");
  const formatData: any[] = [];
  const noSalesArea: string[] = [];
  Object.entries(data)
    .sort(([, a], [, b]) => {
      if (sortKey === "thisMonth") {
        return a.thisMonth > b.thisMonth ? -1 : 1;
      } else {
        return a.lastMonth > b.lastMonth ? -1 : 1;
      }
    })
    .forEach(([category, d]) => {
      if (!d.thisMonth && !d.lastMonth) {
        noSalesArea.push(
          notelClasses.find(
            (notelClass) => notelClass.notelClassId === Number(category)
          )?.name || ""
        );
      } else {
        formatData.push({
          name:
            notelClasses.find(
              (notelClass) => notelClass.notelClassId === Number(category)
            )?.name || "",
          ...d,
        });
      }
    });
  const openModal = () => {
    setNonSalesAreaModal(true, {
      title: t("chart.zeroSales"),
      areaNames: noSalesArea,
    });
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      marginX={2}
      marginY={1}
      width={700}
    >
      <Box display={"flex"} alignItems={"center"}>
        <Typography variant={"subtitle1"}>{t("chart.classMonthCompare")}</Typography>
        <Typography
          variant={"body1"}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            width: "100px",
            margin: "0 10px",
          }}
          onClick={openModal}
        >
          {t("chart.zeroSales")}
        </Typography>
        <TextField
          select
          value={sortKey || ""}
          onChange={(event) => setSortKey(event.target.value as string)}
        >
          <MenuItem value={"thisMonth"} key={"thisMonth"}>
            {t("chart.sortByThisMonth")}
          </MenuItem>
          <MenuItem value={"lastMonth"} key={"lastMonth"}>
            {t("chart.sortByLastMonth")}
          </MenuItem>
        </TextField>
      </Box>
      {formatData.length ? (
        <>
          <BarChart width={700} height={500} data={formatData}>
            <YAxis type="number" width={150} />
            <XAxis
              dataKey="name"
              type="category"
              tick={<CustomizeShopTick />}
              minTickGap={1}
              interval={0}
              height={80}
            />
            <Bar
              dataKey="thisMonth"
              fill="#8884d8"
              name={t("chart.thisMonth")}
              barSize={20}
            />
            <Bar
              dataKey="lastMonth"
              fill="#FCD361"
              name={t("chart.lastMonth")}
              barSize={20}
            />
            <Tooltip formatter={(value: any) => `¥${value}`} />
            {formatData.length > 10 && (
              <Brush height={30} endIndex={10} dataKey={"name"} />
            )}
          </BarChart>
          <NonSalesAreaModal />
        </>
      ) : (
        <Typography>{t("empty.noData")}</Typography>
      )}
    </Box>
  );
};

type SalesVolumeTableProps = {
  title: string;
  header: { key: string; name: string }[];
  data: { name: string; current: number; last: number }[];
};
const SalesVolumeTable = ({ title, header, data }: SalesVolumeTableProps) => {
  const { t } = useTranslation("guestAnalytics");
  const [, setNonSalesAreaModal] = useModal("nonSalesAreaModal");
  const noSalesArea: string[] = [];
  const filterData: any[] = [];
  data.forEach((d) => {
    if (d.current && d.last) {
      filterData.push(d);
    } else {
      noSalesArea.push(d.name);
    }
  });
  const openModal = () => {
    setNonSalesAreaModal(true, {
      title: t("chart.zeroSales"),
      areaNames: noSalesArea,
    });
  };
  return (
    <Box display={"flex"} flexDirection={"column"} width={"700px"}>
      <Box display={"flex"} justifyContent="center" alignItems="center">
        <Typography variant={"subtitle1"}>{title}</Typography>
        <Typography
          variant={"body1"}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            width: "100px",
            margin: "0 10px",
          }}
          onClick={openModal}
        >
          {t("chart.zeroSales")}
        </Typography>
      </Box>
      <TableContainer style={{ maxHeight: 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {header.map((h) => (
                <TableCell key={h.key}>{h.name}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filterData.map((d) => (
              <TableRow key={d.name}>
                <TableCell>{d.name}</TableCell>
                <TableCell>{d.current}</TableCell>
                <TableCell>{d.last}</TableCell>
                <TableCell>{d.current - d.last}</TableCell>
                <TableCell>
                  {d.current && d.last
                    ? MathUtils.roundToTwo((d.current / d.last) * 100)
                    : 0}
                  %
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

function CustomizeShopTick(props: any) {
  const { x, y, payload } = props;
  return (
    <foreignObject x={x - 30} y={y} width="60" height="60">
      <div style={{ fontSize: "10px" }}>{payload.value}</div>
    </foreignObject>
  );
}

const NonSalesAreaModal = () => {
  const [nonSalesAreaModal, setNonSalesAreaModal] =
    useModal("nonSalesAreaModal");

  return (
    <Modal
      show={nonSalesAreaModal.show}
      setShow={setNonSalesAreaModal}
      title={nonSalesAreaModal.meta?.title || ""}
    >
      <Box display={"flex"} flexDirection={"column"}>
        {nonSalesAreaModal.meta?.areaNames?.map((areaName: string) => (
          <Typography key={areaName} style={{ marginTop: "10px" }}>
            {areaName}
          </Typography>
        ))}
      </Box>
    </Modal>
  );
};

export default GuestAnalyticsSalesVolume;
